import PaystackPop from "@paystack/inline-js";
import { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import client from "../../api/client";
import { GlobalContext } from "../../context";
import { currencyFormatter } from "../../utility";

// ─── Tier badge ───────────────────────────────────────────────────────────────

function TierBadge({ tier }) {
  const cfg = {
    full:         { label: "Full Access",   cls: "bg-green-100 text-green-700"  },
    grandfathered:{ label: "Full Access",   cls: "bg-green-100 text-green-700"  },
    partial_high: { label: "60%+ Paid",     cls: "bg-blue-100 text-blue-700"    },
    partial_low:  { label: "Partial",       cls: "bg-amber-100 text-amber-700"  },
    locked:       { label: "Overdue",       cls: "bg-red-100 text-red-700"      },
    none:         { label: "No Access",     cls: "bg-gray-100 text-gray-500"    },
  };
  const c = cfg[tier] || cfg.none;
  return (
    <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${c.cls}`}>
      {c.label}
    </span>
  );
}

// ─── Pay the next instalment (also used by Settings › Billing) ────────────────

export async function payNextInstallment({ sub, email, navigate, onStop }) {
  try {
    const token = localStorage.getItem("nd-rest-tkn");
    if (token) client.defaults.headers.Authorization = `Bearer ${token}`;

    const { data } = await client.post(
      `/subscribers/${sub.subscriber_uuid}/pay-next`
    );

    const popup = new PaystackPop();
    popup.newTransaction({
      key: process.env.REACT_APP_PAYSTACK_KEY,
      access_code: data.access_code,
      email,
      amount: Math.round(data.amount_due * 100),
      currency: sub.currency,
      onSuccess: (response) => {
        navigate(`/checkout/callback?reference=${response.reference}`);
      },
      onCancel: () => {
        toast.info("Payment cancelled.");
        onStop();
      },
    });
  } catch (err) {
    const msg = err?.response?.data?.message || "Could not initiate payment.";
    toast.error(msg);
    onStop();
  }
}

// ─── Single subscription card ─────────────────────────────────────────────────

function SubscriptionCard({ sub }) {
  const { authState } = useContext(GlobalContext);
  const navigate = useNavigate();
  const [paying, setPaying] = useState(false);

  const fmt = (amt) => currencyFormatter(amt, { currency: sub.currency });
  const pct = Math.min(100, Math.round(sub.payment_pct));
  const isFullyPaid = pct >= 100 || sub.access_tier === "full" || sub.access_tier === "grandfathered";
  const isPartial = sub.status === "partial" && sub.can_pay_next;

  const handlePayNext = () => {
    setPaying(true);
    payNextInstallment({ sub, email: authState?.data?.user?.email, navigate, onStop: () => setPaying(false) });
  };

  return (
    <div className="bg-white rounded-2xl border border-gray-100 p-5 md:p-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-3 mb-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
              sub.course_type === "live"
                ? "bg-[#294a93]/10 text-[#294a93]"
                : "bg-purple-100 text-purple-700"
            }`}>
              {sub.course_type === "live" ? "Live Course" : "Online Course"}
            </span>
            <TierBadge tier={sub.access_tier} />
            {sub.is_overdue && (
              <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-red-100 text-red-600">
                Overdue
              </span>
            )}
          </div>
          <h3 className="font-bold text-gray-900 text-base leading-snug">{sub.course_title}</h3>
        </div>
      </div>

      {/* Progress bar */}
      <div className="mb-4">
        <div className="flex items-center justify-between text-xs text-gray-500 mb-1.5">
          <span>Payment progress</span>
          <span className="font-semibold text-gray-700">{pct}%</span>
        </div>
        <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
          <div
            className={`h-full rounded-full transition-all duration-500 ${
              pct >= 100 ? "bg-green-500" : pct >= 60 ? "bg-[#d15424]" : "bg-amber-400"
            }`}
            style={{ width: `${pct}%` }}
          />
        </div>
      </div>

      {/* Amounts */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-4">
        <div className="bg-gray-50 rounded-xl p-3">
          <p className="text-xs text-gray-400 mb-0.5">Paid</p>
          <p className="font-bold text-gray-800 text-sm">{fmt(sub.amount_paid)}</p>
        </div>
        <div className="bg-gray-50 rounded-xl p-3">
          <p className="text-xs text-gray-400 mb-0.5">Total</p>
          <p className="font-bold text-gray-800 text-sm">{fmt(sub.total_price)}</p>
        </div>
        {sub.total_price > sub.amount_paid && (
          <div className="bg-gray-50 rounded-xl p-3">
            <p className="text-xs text-gray-400 mb-0.5">Remaining</p>
            <p className="font-bold text-[#d15424] text-sm">{fmt(sub.total_price - sub.amount_paid)}</p>
          </div>
        )}
      </div>

      {/* Installment info */}
      {sub.total_installments && (
        <div className="flex items-center gap-2 mb-4">
          <div className="flex gap-1">
            {Array.from({ length: sub.total_installments }).map((_, i) => (
              <div
                key={i}
                className={`h-2 w-8 rounded-full ${
                  i < (sub.installments_paid || 0) ? "bg-[#d15424]" : "bg-gray-200"
                }`}
              />
            ))}
          </div>
          <span className="text-xs text-gray-500">
            {sub.installments_paid || 0} of {sub.total_installments} installment
            {sub.total_installments !== 1 ? "s" : ""} paid
          </span>
        </div>
      )}

      {/* Days since payment */}
      {sub.days_since_payment != null && sub.status === "partial" && (
        <p className={`text-xs mb-4 ${sub.days_since_payment >= 30 ? "text-red-600 font-semibold" : "text-gray-400"}`}>
          Last payment: {sub.days_since_payment === 0 ? "today" : `${sub.days_since_payment} day${sub.days_since_payment !== 1 ? "s" : ""} ago`}
          {sub.days_since_payment >= 30 && " — next payment overdue"}
        </p>
      )}

      {/* Actions */}
      <div className="flex gap-2 flex-wrap">
        <button
          onClick={() => navigate(`/dashboard/course/${sub.course_uuid}`)}
          className="flex-1 min-w-[120px] border-2 border-[#294a93] text-[#294a93] font-semibold
            text-sm py-2.5 rounded-xl hover:bg-[#294a93]/5 transition-colors"
        >
          Open Course
        </button>
        {isPartial && (
          <button
            onClick={handlePayNext}
            disabled={paying}
            className="flex-1 min-w-[160px] bg-[#d15424] hover:bg-[#b8451d] disabled:opacity-60
              text-white font-semibold text-sm py-2.5 rounded-xl transition-colors flex items-center
              justify-center gap-2"
          >
            {paying ? (
              <>
                <span className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                Initializing…
              </>
            ) : (
              <>
                Pay Installment {(sub.installments_paid || 0) + 1}
                {sub.amount_per_install ? ` — ${fmt(sub.amount_per_install)}` : ""}
              </>
            )}
          </button>
        )}
        {isFullyPaid && (
          <span className="flex-1 min-w-[120px] text-center bg-green-50 text-green-700 font-semibold
            text-sm py-2.5 rounded-xl">
            Fully Paid ✓
          </span>
        )}
      </div>
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function SubscriptionsPage() {
  const [subs, setSubs]     = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem("nd-rest-tkn");
    if (token) client.defaults.headers.Authorization = `Bearer ${token}`;

    client
      .get("/subscribers/my-access-status")
      .then((res) => setSubs(res.data?.subscriptions || []))
      .catch(() => toast.error("Could not load subscriptions."))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="py-6 px-4 md:px-8">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">My Subscriptions</h1>
        <p className="text-sm text-gray-500 mt-1">
          Track your payments and make installment payments here.
        </p>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-24">
          <div className="w-8 h-8 border-2 border-gray-200 border-t-[#d15424] rounded-full animate-spin" />
        </div>
      ) : subs.length === 0 ? (
        <div className="bg-white rounded-2xl border border-gray-100 p-12 text-center">
          <div className="text-5xl mb-3">📚</div>
          <h2 className="font-semibold text-gray-700 mb-1">No active subscriptions</h2>
          <p className="text-sm text-gray-400 mb-6">
            Enroll in a course to get started.
          </p>
          <a
            href="/dashboard/enroll"
            className="inline-flex bg-[#d15424] hover:bg-[#b8451d] text-white font-semibold
              text-sm px-6 py-3 rounded-xl transition-colors"
          >
            Browse Courses
          </a>
        </div>
      ) : (
        <div className="flex flex-col gap-4 max-w-3xl">
          {subs.map((sub) => (
            <SubscriptionCard key={sub.subscriber_uuid} sub={sub} />
          ))}
        </div>
      )}
    </div>
  );
}
